<script setup lang="ts">
// Opening my node to other people (HRTH-182, docs/STORAGE.md §11.3): upload limits for spaces,
// their roles and people, which they keep libraries of their own within. The limits are sealed
// for the node, which enforces them; the server learns only who they name.
import { computed, onMounted, reactive, ref } from 'vue'
import { describeError } from '../api/http'
import type { StorageLocation } from '../api/types'
import { useSession } from '../stores/session'
import { grantees } from '../lib/memberStorage'
import { limitFromText, limitText, noRules, type NodeRules } from '../lib/nodeShare'

const props = defineProps<{ loc: StorageLocation; name: string }>()
const emit = defineEmits<{ saved: []; cancel: [] }>()
const session = useSession()

type Section = keyof NodeRules
/** What's ticked, and the limit typed for it (GB; blank: no limit). */
const form = reactive<Record<Section, Record<string, { on: boolean; gb: string }>>>({ people: {}, spaces: {}, roles: {} })
const busy = ref(false)
const loaded = ref(false)

const spaces = computed(() => session.spaces.filter((s) => !s.host))
const people = computed(() => grantees(session.profiles.values(), session.me))

const row = (section: Section, id: string) => (form[section][id] ??= { on: false, gb: '' })

onMounted(async () => {
  for (const s of spaces.value) {
    row('spaces', s.id)
    for (const r of s.roles ?? []) row('roles', r.id)
  }
  for (const p of people.value) row('people', p.user_id)
  try {
    const rules = await session.engine!.nodeRules(props.loc)
    for (const section of ['people', 'spaces', 'roles'] as const) {
      for (const [id, mb] of Object.entries(rules[section])) form[section][id] = { on: true, gb: limitText(mb) }
    }
  } catch (e) {
    session.toast(describeError(e), 'error')
  }
  loaded.value = true
})

/** The rules as ticked, or null if a limit isn't a number. */
const rules = computed<NodeRules | null>(() => {
  const out = noRules()
  for (const section of ['people', 'spaces', 'roles'] as const) {
    for (const [id, r] of Object.entries(form[section])) {
      if (!r.on) continue
      const mb = limitFromText(r.gb)
      if (mb === undefined) return null
      out[section][id] = mb
    }
  }
  return out
})

async function save(): Promise<void> {
  if (!rules.value) return
  busy.value = true
  try {
    await session.engine!.openNode(props.loc.id, rules.value)
    session.toast(Object.values(form).some((s) => Object.values(s).some((r) => r.on)) ? `${props.name} is open to them` : `${props.name} is closed to others`)
    emit('saved')
  } catch (e) {
    session.toast(describeError(e), 'error')
  } finally {
    busy.value = false
  }
}
</script>

<template>
  <form class="mt-2 space-y-3 border border-border p-3" data-testid="node-share" @submit.prevent="save">
    <p class="mono text-text-muted">
      Let people keep libraries of their own on {{ name }}, up to a limit each (all their libraries there together). A
      person's own limit comes first, else the largest of their spaces' and roles'. Leave a limit blank for none; 0
      shuts someone out. Their files are encrypted on their devices: this computer keeps them but can't read them.
      Only your devices and the node can read the limits.
    </p>
    <template v-if="loaded">
      <div class="label">Spaces and roles</div>
      <p v-if="!spaces.length" class="mono text-text-muted">You aren't in any spaces on this server.</p>
      <template v-for="s in spaces" :key="s.id">
        <div class="flex items-center gap-3 text-sm">
          <label class="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
            <input v-model="row('spaces', s.id).on" type="checkbox" :data-testid="`node-share-space-${s.name}`" />
            <span class="truncate">{{ s.name }}</span>
          </label>
          <input
            v-model="row('spaces', s.id).gb"
            class="input w-24 text-right"
            inputmode="decimal"
            placeholder="no limit"
            :disabled="!row('spaces', s.id).on"
            :aria-label="`Limit for ${s.name}, GB`"
            :data-testid="`node-share-space-gb-${s.name}`"
          />
          <span class="mono w-6 text-text-muted">GB</span>
        </div>
        <div v-for="r in s.roles ?? []" :key="r.id" class="flex items-center gap-3 pl-7 text-sm">
          <label class="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
            <input v-model="row('roles', r.id).on" type="checkbox" :data-testid="`node-share-role-${s.name}-${r.name}`" />
            <span class="h-1.5 w-1.5 shrink-0" :style="{ background: r.color }" />
            <span class="mono truncate text-text-secondary uppercase">{{ r.name }}</span>
          </label>
          <input
            v-model="row('roles', r.id).gb"
            class="input w-24 text-right"
            inputmode="decimal"
            placeholder="no limit"
            :disabled="!row('roles', r.id).on"
            :aria-label="`Limit for ${r.name} in ${s.name}, GB`"
          />
          <span class="mono w-6 text-text-muted">GB</span>
        </div>
      </template>
      <div class="label">People</div>
      <p v-if="!people.length" class="mono text-text-muted">People you share a space or conversation with appear here.</p>
      <div v-for="p in people" :key="p.user_id" class="flex items-center gap-3 text-sm">
        <label class="flex min-w-0 flex-1 cursor-pointer items-center gap-3">
          <input v-model="row('people', p.user_id).on" type="checkbox" :data-testid="`node-share-person-${p.user_id}`" />
          <span class="truncate">{{ p.display_name }}</span>
        </label>
        <input
          v-model="row('people', p.user_id).gb"
          class="input w-24 text-right"
          inputmode="decimal"
          placeholder="no limit"
          :disabled="!row('people', p.user_id).on"
          :aria-label="`Limit for ${p.display_name}, GB`"
          :data-testid="`node-share-person-gb-${p.user_id}`"
        />
        <span class="mono w-6 text-text-muted">GB</span>
      </div>
      <p v-if="!rules" class="mono text-signal">Limits are a number of GB, 0 or more, or blank for none.</p>
    </template>
    <div class="flex justify-end gap-2">
      <button type="button" class="btn-ghost" @click="emit('cancel')">Cancel</button>
      <button class="btn-primary" :disabled="busy || !loaded || !rules" data-testid="node-share-save">Save</button>
    </div>
  </form>
</template>
